class InfiniteCollection extends HTMLElement {
  constructor() {
    super();
    this.currentPage = 1;
    this.totalPages = 1;
    this.isLoading = false;
    this.sentinel = null;
    this.loadingIndicator = null;
    this.productGrid = null;
    this.observer = null;
    this.collectionHandle = null;
    this.productsPerPage = 16;
  }

  connectedCallback() {
    this.collectionHandle = this.dataset.collectionHandle;
    this.productsPerPage = parseInt(this.dataset.productsPerPage) || 16;
    
    this.productGrid = this.querySelector('[id^="InfiniteCollection-"]');
    this.sentinel = this.querySelector('.infinite-collection-sentinel');
    this.loadingIndicator = this.querySelector('.infinite-collection-loading');

    if (!this.sentinel) return;

    this.currentPage = parseInt(this.sentinel.dataset.currentPage) || 1;
    this.totalPages = parseInt(this.sentinel.dataset.totalPages) || 1;

    if (this.currentPage >= this.totalPages) {
      if (this.sentinel) this.sentinel.style.display = 'none';
      return;
    }

    this.initIntersectionObserver();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  initIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '200px', // 提前200px开始加载（不要太激进）
      threshold: 0.1       // 10%可见才触发
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.isLoading && this.currentPage < this.totalPages) {
          this.loadNextPage();
        }
      });
    }, options);

    this.observer.observe(this.sentinel);
  }

  async loadNextPage() {
    if (this.isLoading || this.currentPage >= this.totalPages) {
      return;
    }

    this.isLoading = true;
    this.showLoading();

    const nextPage = this.currentPage + 1;
    let nextPageUrl = this.sentinel.dataset.nextPageUrl;
    const collectionUrl = this.sentinel.dataset.collectionUrl;

    if (collectionUrl) {
      try {
        const url = new URL(collectionUrl, window.location.origin);
        url.searchParams.set('page', nextPage.toString());
        nextPageUrl = url.toString();
      } catch (e) {
        if (nextPageUrl && nextPageUrl.startsWith('/')) {
          nextPageUrl = window.location.origin + nextPageUrl;
        }
      }
    } else if (nextPageUrl && nextPageUrl.startsWith('/')) {
      nextPageUrl = window.location.origin + nextPageUrl;
    }

    if (!nextPageUrl) {
      this.hideLoading();
      this.isLoading = false;
      return;
    }

    try {
      const response = await fetch(nextPageUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      let newProductGrid = doc.querySelector('infinite-collection [id^="InfiniteCollection-"]');
      if (!newProductGrid) {
        newProductGrid = doc.querySelector('[id^="InfiniteCollection-"]');
      }
      if (!newProductGrid) {
        newProductGrid = doc.querySelector('.product-grid');
      }
      if (!newProductGrid) {
        throw new Error('无法找到产品网格');
      }
      
      const newProducts = Array.from(newProductGrid.querySelectorAll('.grid__item'));
      if (newProducts.length === 0) {
        throw new Error('没有找到新产品');
      }

      const currentProductCount = this.productGrid.querySelectorAll('.grid__item').length;
      const columnsDesktop = this.getDesktopColumns();
      
      newProducts.forEach((product, index) => {
        const globalIndex = currentProductCount + index + 1;
        
        // 更新lazy_load属性
        const images = product.querySelectorAll('img[loading="lazy"]');
        if (globalIndex <= columnsDesktop) {
          images.forEach(img => {
            img.loading = 'eager';
            img.removeAttribute('loading');
          });
        }

        const productId = product.id;
        if (productId) {
          product.id = productId.replace(/-(\d+)$/, `-${globalIndex}`);
        }

        if (product.hasAttribute('style') && product.style.getPropertyValue('--animation-order')) {
          product.style.setProperty('--animation-order', globalIndex);
        }

        // 移除滚动动画类，因为这些产品是通过 AJAX 加载的，不需要动画
        product.classList.remove('scroll-trigger--offscreen');
        
        // 或者立即将它们标记为已动画，确保可见
        if (product.classList.contains('scroll-trigger')) {
          product.classList.add('scroll-trigger--cancel');
          product.style.opacity = '1';
        }

        this.productGrid.appendChild(product);
      });

      this.currentPage = nextPage;
      this.sentinel.dataset.currentPage = nextPage;
      
      if (this.currentPage >= this.totalPages) {
        this.sentinel.style.display = 'none';
        if (this.observer) {
          this.observer.disconnect();
        }
      }

      if (typeof initializeScrollAnimationTrigger === 'function') {
        try {
          initializeScrollAnimationTrigger(this.productGrid);
        } catch (e) {
          // Silently fail
        }
      }

      // 触发自定义事件，通知其他脚本
      this.dispatchEvent(new CustomEvent('infiniteCollection:loaded', {
        detail: {
          page: this.currentPage,
          productsLoaded: newProducts.length
        }
      }));

    } catch (error) {
      // Silently handle errors
    } finally {
      this.hideLoading();
      this.isLoading = false;
    }
  }

  showLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.add('active');
    }
  }

  hideLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.remove('active');
    }
  }

  getDesktopColumns() {
    // 从grid类名中提取列数，例如 grid--4-col-desktop
    const gridClasses = this.productGrid.className;
    const match = gridClasses.match(/grid--(\d+)-col-desktop/);
    return match ? parseInt(match[1]) : 4;
  }
}

// 使用自定义元素名称注册
if (typeof customElements !== 'undefined') {
  customElements.define('infinite-collection', InfiniteCollection);
}

